import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import intelligentSearchService from '../services/intelligentSearchService';

/**
 * Hook personnalisé pour les fonctionnalités d'optimisation de recherche
 * Utilise les nouveaux services backend (cache, personnalisation, analytics)
 */
const useSearchOptimization = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [realtimeMetrics, setRealtimeMetrics] = useState(null);
  const [cacheStats, setCacheStats] = useState(null);
  const [personalization, setPersonalization] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Référence pour éviter les appels multiples
  const metricsIntervalRef = useRef(null);

  /**
   * Charger les analytics de recherche
   */
  const loadAnalytics = useCallback(async (timeframe = '24h') => {
    try {
      setIsLoading(true);
      setError(null);
      
      const analyticsData = await intelligentSearchService.getSearchAnalytics(timeframe);
      setAnalytics(analyticsData);
      
    } catch (err) {
      console.error('[useSearchOptimization] Error loading analytics:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Charger les métriques en temps réel
   */
  const loadRealtimeMetrics = useCallback(async () => {
    try {
      const metrics = await intelligentSearchService.getRealtimeMetrics();
      setRealtimeMetrics(metrics);
    } catch (err) {
      console.error('[useSearchOptimization] Error loading realtime metrics:', err);
    }
  }, []);

  /**
   * Charger les statistiques de cache
   */
  const loadCacheStats = useCallback(async () => {
    try {
      const stats = await intelligentSearchService.getCacheStats();
      setCacheStats(stats);
    } catch (err) {
      console.error('[useSearchOptimization] Error loading cache stats:', err);
    }
  }, []);

  /**
   * Charger la personnalisation utilisateur
   */
  const loadPersonalization = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const userPersonalization = await intelligentSearchService.getUserPersonalization(user.id);
      setPersonalization(userPersonalization);
    } catch (err) {
      console.error('[useSearchOptimization] Error loading personalization:', err);
    }
  }, [user?.id]);

  /**
   * Tracker une interaction utilisateur
   */
  const trackInteraction = useCallback(async (interactionData) => {
    try {
      await intelligentSearchService.trackInteraction({
        ...interactionData,
        userId: user?.id
      });
    } catch (err) {
      console.error('[useSearchOptimization] Error tracking interaction:', err);
    }
  }, [user?.id]);

  /**
   * Effectuer une recherche optimisée avec cache et apprentissage
   */
  const performOptimizedSearch = useCallback(async (query, filters = {}) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const userPreferences = user?.preferences || {};
      const result = await intelligentSearchService.searchWithLearning(
        query, 
        filters, 
        userPreferences
      );
      
      return result;
      
    } catch (err) {
      console.error('[useSearchOptimization] Error performing optimized search:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [user?.preferences]);

  /**
   * Obtenir des suggestions personnalisées
   */
  const getPersonalizedSuggestions = useCallback(async (query) => {
    if (!user?.id || !query) return [];
    
    try {
      const suggestions = await intelligentSearchService.getPersonalizedSuggestions(query, user.id);
      return suggestions;
    } catch (err) {
      console.error('[useSearchOptimization] Error getting personalized suggestions:', err);
      return [];
    }
  }, [user?.id]);

  /**
   * Invalider le cache utilisateur
   */
  const invalidateCache = useCallback(async () => {
    if (!user?.id) return false;
    
    try {
      const success = await intelligentSearchService.invalidateUserCache(user.id);
      if (success) {
        // Recharger les stats après invalidation
        await loadCacheStats();
      }
      return success;
    } catch (err) {
      console.error('[useSearchOptimization] Error invalidating cache:', err);
      return false;
    }
  }, [user?.id, loadCacheStats]);

  /**
   * Démarrer le monitoring en temps réel
   */
  const startRealtimeMonitoring = useCallback((intervalMs = 5000) => {
    if (metricsIntervalRef.current) {
      clearInterval(metricsIntervalRef.current);
    }
    
    // Charger immédiatement
    loadRealtimeMetrics();
    
    // Puis charger périodiquement
    metricsIntervalRef.current = setInterval(loadRealtimeMetrics, intervalMs);
  }, [loadRealtimeMetrics]);

  /**
   * Arrêter le monitoring en temps réel
   */
  const stopRealtimeMonitoring = useCallback(() => {
    if (metricsIntervalRef.current) {
      clearInterval(metricsIntervalRef.current);
      metricsIntervalRef.current = null;
    }
  }, []);

  /**
   * Obtenir les métriques locales
   */
  const getLocalMetrics = useCallback(() => {
    return intelligentSearchService.getLocalMetrics();
  }, []);

  /**
   * Nettoyer le cache local
   */
  const clearLocalCache = useCallback(() => {
    intelligentSearchService.clearLocalCache();
    loadCacheStats(); // Recharger les stats
  }, [loadCacheStats]);

  // Charger les données initiales
  useEffect(() => {
    if (user?.id) {
      // ❌ Ne plus charger automatiquement la personnalisation pour éviter l'erreur 403
      // loadPersonalization(); // Sera chargé à la demande via le bouton "Charger Préférences"
      loadCacheStats();
    }
  }, [user?.id, loadCacheStats]);

  // Nettoyer l'interval à la destruction du composant
  useEffect(() => {
    return () => {
      stopRealtimeMonitoring();
    };
  }, [stopRealtimeMonitoring]);

  return {
    // État
    analytics,
    realtimeMetrics,
    cacheStats,
    personalization,
    isLoading,
    error,
    
    // Actions
    loadAnalytics,
    loadRealtimeMetrics,
    loadCacheStats,
    loadPersonalization,
    trackInteraction,
    performOptimizedSearch,
    getPersonalizedSuggestions,
    invalidateCache,
    startRealtimeMonitoring,
    stopRealtimeMonitoring,
    getLocalMetrics,
    clearLocalCache,
    
    // Utilitaires
    hasAnalytics: !!analytics,
    hasRealtimeMetrics: !!realtimeMetrics,
    hasCacheStats: !!cacheStats,
    hasPersonalization: !!personalization
  };
};

export default useSearchOptimization;
